import React, { useState } from "react";
import { useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
  Modal,
  Backdrop,
  Fade,
} from "@mui/material";
import Navbar from "../Navbar/Navbar";
import homepagevideo from "../assets/homepagevideo.mp4";
import crewManagement from "../assets/crewManagement.jpg";
import technicalSupport from "../assets/technicalSupport.png";
import shipOperations from "../assets/shipOperations.jpg";
import { useLocation } from "react-router-dom";

const HomePage = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [termsOpen, setTermsOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
  if (location.state?.scrollTo) {
    const section = document.getElementById(location.state.scrollTo);
    if (section) {
      setTimeout(() => {
        section.scrollIntoView({ behavior: "smooth" });
      }, 300); // small delay for page load
    }
  }
}, [location]);


  return (
    <Box>
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <Box
        id="hero"
        sx={{
          position: "relative",
          height: { xs: "70vh", md: "90vh" },
          overflow: "hidden",
          color: "#fff",
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        >
          <source src={homepagevideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Overlay Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.0)",
            px: 2,
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant={isSmall ? "h4" : "h2"}
              fontWeight="bold"
              gutterBottom
              color="#C30B12"
            >
              CrewGenix
            </Typography>
            <Typography
              variant={isSmall ? "body1" : "h6"}
              sx={{ mb: 4 }}
              color="#FFD007"
            >
              CrewGenix is a cutting-edge maritime platform dedicated to bridging
              the gap between vessel owners , crew and crew manning agencies. Our mission
              is to facilitate efficient, transparent, and reliable crew management
              solutions. With a team of industry experts and a user-centric
              approach, we are committed to transforming the maritime industry
              through innovative technology and integrated services.
            </Typography>
          </Container>
        </Box>
      </Box>

      {/* Our Services Section */}
      <Box
        id="services"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: "#f9f9f9" }}
      >
        <Container>
          <Typography
            variant={isSmall ? "h5" : "h4"}
            textAlign="center"
            gutterBottom
            fontWeight="bold"
            color="#C30B12"
          >
            Our Services
          </Typography>
          <Typography textAlign="center" sx={{ mb: 4 }}>
            We offer a range of professional marine services to ensure smooth
            sailing.
          </Typography>

          {/* Cards */}
          <Grid container spacing={4}>
            {[
              {
                img: crewManagement,
                title: "Vessel Crew Management",
                desc: "Simplify crew recruitment, onboarding, and management with our comprehensive tools.",
              },
              {
                img: technicalSupport,
                title: "Crew Tracking & Communication",
                desc: "Real-time updates and direct communication channels for seamless operations.",
              },
              {
                img: shipOperations,
                title: "Documentation & Compliance",
                desc: "Ensure all crew documents and certifications are up-to-date and compliant with maritime regulations.",
              },
              {
                img: shipOperations,
                title: "Reporting & Analytics",
                desc: "Gain insights with detailed reports on crew performance, rotations, and vessel operations.",
              },
              {
                img: shipOperations,
                title: "Custom Solutions",
                desc: "Tailored services to meet specific vessel and fleet requirements.",
              },
            ].map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    background: "#fff",
                    p: 3,
                    borderRadius: 4,
                    boxShadow: 3,
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  <img
                    src={service.img}
                    alt={service.title}
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "16px",
                      marginBottom: "1rem",
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold" color="#C30B12">
                    {service.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {service.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Us */}
      <Box id="about" sx={{ padding: 4, pt: 10, backgroundColor: "#C30B12", textAlign: "center" }}>
        <Typography variant="h4" gutterBottom color="#FBC704">
          About Us
        </Typography>
        <Typography variant="body1" paragraph color="white">
          CrewGenix is a cutting-edge maritime platform dedicated to bridging
          the gap between vessel owners , crew and crew manning agencies. Our mission
          is to facilitate efficient, transparent, and reliable crew management
          solutions. With a team of industry experts and a user-centric
          approach, we are committed to transforming the maritime industry
          through innovative technology and integrated services.
        </Typography>
      </Box>

      {/* Vision */}
      <Box sx={{ padding: 4, backgroundColor: "#C30B12", textAlign: "center" }}>
        <Typography variant="h4" gutterBottom color="#FBC704">
          Vision
        </Typography>
        <Typography variant="body1" paragraph color="white">
          To be the leading digital platform in the maritime industry,
          empowering vessel owners and crew agents worldwide with innovative,
          reliable, and accessible crew management solutions.
        </Typography>
      </Box>

      {/* Mission */}
      <Box sx={{ padding: 4, backgroundColor: "#C30B12", textAlign: "center" }}>
        <Typography variant="h4" gutterBottom color="#FBC704">
          Mission
        </Typography>
        <Typography variant="body1" paragraph color="white">
          Our mission is to create a unified, efficient, and transparent
          platform that enhances crew management processes, reduces operational
          costs, and promotes safety and compliance in maritime operations.
        </Typography>
      </Box>
      <Box
        id="contact"
        sx={{
          padding: 4,
          paddingLeft: 2,
          paddingRight: 2,
          backgroundColor: "#fff",
          width: "100%",      // Full width
          boxSizing: "border-box", // Ensure padding doesn't cause overflow
          textAlign: "center",
        }}
      >
        <Typography variant="h4" gutterBottom color="#C30B12">
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          Have questions? Feel free to reach out to us at:
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Email:</strong> business@crewgenix.com
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Phone:</strong> +971 554168425
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Address:</strong> 209, Arzu Building AI Qusais, Dubai, UAE.
        </Typography>
      </Box>


      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: "#0d1b2a",
          color: "#fff",
          py: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          &copy; 2025 CrewGenix. All rights reserved.
        </Typography>
        <Link
          onClick={() => setTermsOpen(true)}
          sx={{
            cursor: "pointer",
            color: "#90caf9",
            textDecoration: "underline",
            fontWeight: "bold",
          }}
          underline="hover"
          component="button"
          aria-label="Open terms and conditions"
        >
          Terms & Conditions
        </Link>
      </Box>

      {/* Terms & Conditions Modal */}
      <Modal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={termsOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 500 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" gutterBottom>
              Terms & Conditions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              By accessing and using CrewGenix, users agree to adhere to our
              terms and conditions designed to ensure fair and lawful use of
              the platform. Users must provide accurate information and uphold
              confidentiality and security standards.
            </Typography>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default HomePage;
